/** Single field-level validation failure from api-contracts §4. */
export interface ApiErrorDetail {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

/** RFC-7807-style error body from api-contracts §4. */
export interface ApiErrorBody {
  readonly error: string;
  readonly code: string;
  readonly timestamp: string;
  readonly path: string;
  readonly details?: readonly ApiErrorDetail[];
  /**
   * RFC-9457 free-text `detail` member, forwarded verbatim from the server (e.g.
   * `UNGUELTIGER_AUFTRAGSWERT`'s "Field [...] has value [...] which is not a member of its closed
   * set." message). Not a new extension — `detail` is a standard `application/problem+json`
   * member; it was simply never consumed before this field was added. Never used as the dispatch
   * key for error handling (ADR-SW-026) — `code` alone decides which branch runs; `detail` may
   * only be inspected for supplementary structured data (e.g. the field path) once `code` has
   * already selected that branch.
   */
  readonly detail?: string;
}

/** Typed error thrown by the HTTP client for every non-2xx response. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;
  readonly correlationId: string | null;

  constructor(status: number, body: ApiErrorBody, correlationId: string | null) {
    super(body.error);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.correlationId = correlationId;
  }
}
